// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DataGrid, { Column } from "react-data-grid";
import AutoSizer from "react-virtualized-auto-sizer";

import { WarningOutlined } from "@ant-design/icons";
import { mdiDelete } from "@mdi/js";
import { Alert, Button, Empty, Popconfirm, Spin, Tooltip } from "antd";
import { DEPTH_MISSING_WARNING } from "constants/scenario.constants";
import styled from "styled-components";

import HeaderButtons from "./HeaderButtons";
import SetStickDepthModal from "./SetStickDepthModal";
import { usePadScenario } from "./contexts/usePadScenario";
import useDeleteStick from "./hooks/useDeleteStick";
import { Stick } from "./models/scenario";
import { sticksHaveDepth } from "./utils/scenarioHelpers";

interface ScenarioSticksProps {
  showFullPadScenario?: boolean;
  isStickManagementMode?: boolean;
}

const RootContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;

  > .ant-alert {
    flex-shrink: 0;
  }
`;

const GridContainer = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;

  .ant-spin-nested-loading,
  .ant-spin-container {
    height: 100%;
  }

  .rdg {
    --rdg-border-color: #f0f0f0;
    --rdg-header-background-color: #fafafa;
    --rdg-row-hover-background-color: #fafafa;
    font-size: 1.3rem;
    color: rgba(0, 0, 0, 0.85);
  }

  .rdg-cell {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    line-height: 1.5715;
  }

  .rdg-header-row {
    font-weight: 500;
  }

  .stick-actions {
    justify-content: center;
  }
`;

const StickNameCell = styled.div`
  min-width: 0;
  width: 100%;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

function DeleteStickButton({
  stickName,
  isCellSelected,
  onConfirm
}: {
  stickName: string;
  isCellSelected: boolean;
  onConfirm: () => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (isCellSelected) buttonRef.current?.focus({ preventScroll: true });
  }, [isCellSelected]);

  return (
    <ButtonWrapper>
      <Popconfirm
        title="Are you sure you want to remove this stick?"
        okText="Remove"
        cancelText="Cancel"
        onConfirm={onConfirm}>
        <Button
          ref={buttonRef}
          aria-label={`Remove ${stickName}`}
          size="small"
          tabIndex={isCellSelected ? 0 : -1}
          onKeyDown={(event) => {
            // Let the button activate without the grid consuming Enter/Space.
            if (event.key === "Enter" || event.key === " ") event.stopPropagation();
          }}
          type="primary"
          danger
          icon={<Icon path={mdiDelete} size={1.2} />}
        />
      </Popconfirm>
    </ButtonWrapper>
  );
}

const EmptySticks = () => (
  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ gridColumn: "1 / -1" }} />
);

export default function ScenarioSticks({
  showFullPadScenario,
  isStickManagementMode
}: ScenarioSticksProps) {
  const { state } = usePadScenario();
  const { sticks, stickData, scenarioFields, isLoadingStickData, stickDataError } = state;
  const scenarioId = state.scenario?._id?.$oid;
  const scenarioName = state.scenario?.scenarioId;
  const canManage = showFullPadScenario || isStickManagementMode;
  const hasDataError = showFullPadScenario && !!stickDataError;
  const { mutate: deleteStick, isLoading: isDeletingStick } = useDeleteStick();
  const [setDepthOpen, setSetDepthOpen] = useState(false);

  const sticksMissingDepth = useMemo(() => {
    const missing = new Set<Stick>();
    for (const stick of sticks) {
      if (!sticksHaveDepth([stick])) missing.add(stick);
    }
    return missing;
  }, [sticks]);

  // Field responses follow the full survey order, independent of the visible window.
  const stickIndex = useMemo(
    () => new Map(sticks.map((stick, index) => [stick, index])),
    [sticks]
  );

  // Display IDs can repeat. Reference keys also survive reordering the same records.
  const rowKeys = useRef(new WeakMap<Stick, number>());
  const nextRowKey = useRef(0);
  const rowKeyGetter = useCallback((stick: Stick) => {
    let key = rowKeys.current.get(stick);
    if (key === undefined) {
      key = nextRowKey.current++;
      rowKeys.current.set(stick, key);
    }
    return key;
  }, []);

  const columns = useMemo<Column<Stick>[]>(() => {
    const nameColumn: Column<Stick> = {
      name: "Stick Name",
      key: "id",
      width: 300,
      frozen: true,
      formatter: ({ row }) => (
        <StickNameCell>
          <span title={row.id}>{row.id}</span>
          {sticksMissingDepth.has(row) && (
            <Tooltip title="This stick is missing depth (Z) values">
              <WarningOutlined
                style={{ color: "#faad14", fontSize: 13, flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </StickNameCell>
      )
    };
    if (!canManage) return [{ ...nameColumn, minWidth: 300, width: "100%" }];

    const gridColumns: Column<Stick>[] = [
      nameColumn,
      { name: "Ring Fence", key: "ringFenceName", width: 150 },
      { name: "Type Wells", key: "typeWellName", width: 150 },
      { name: "Length", key: "hzLength_m", width: 150 },
      ...scenarioFields.map(
        (field): Column<Stick> => ({
          name: field.name,
          key: `field:${field.field}`,
          width: 180,
          formatter: ({ row }) => {
            const value = stickData?.[stickIndex.get(row)]?.[field.field];
            if (value === null || value === undefined) {
              return (
                <span style={{ color: "#d9d9d9", width: "100%", textAlign: "center" }}>
                  —
                </span>
              );
            }
            return (
              <span>{typeof value === "number" ? value.toFixed(2) : String(value)}</span>
            );
          }
        })
      ),
      {
        name: "Action",
        key: "delete",
        width: 50,
        cellClass: "stick-actions",
        headerCellClass: "stick-actions",
        formatter: ({ row, isCellSelected }) => (
          <DeleteStickButton
            stickName={row.id}
            isCellSelected={isCellSelected}
            onConfirm={() =>
              deleteStick({ scenarioId: scenarioId ?? "", stickId: row.id })
            }
          />
        )
      }
    ];
    // Match the previous table's proportional widths, retaining horizontal scroll
    // once the viewport is narrower than the columns' minimum widths.
    const totalWidth = gridColumns.reduce((sum, column) => sum + Number(column.width), 0);
    return gridColumns.map((column, index) => ({
      ...column,
      minWidth: Number(column.width),
      width:
        index === gridColumns.length - 1
          ? undefined
          : `${Math.round((Number(column.width) / totalWidth) * 100)}%`
    }));
  }, [
    canManage,
    deleteStick,
    scenarioFields,
    scenarioId,
    stickData,
    stickIndex,
    sticksMissingDepth
  ]);

  return (
    <RootContainer>
      {canManage && (
        <HeaderButtons
          scenarioId={scenarioId}
          scenarioName={scenarioName}
          stickData={hasDataError ? undefined : stickData}
          sticks={sticks}
        />
      )}
      {sticksMissingDepth.size > 0 && (
        <Alert
          message={DEPTH_MISSING_WARNING}
          type="warning"
          showIcon
          style={{ margin: "8px 0" }}
          action={
            <Button size="small" onClick={() => setSetDepthOpen(true)}>
              Set Depth
            </Button>
          }
        />
      )}
      {scenarioId && (
        <SetStickDepthModal
          key={scenarioId}
          open={setDepthOpen}
          scenarioId={scenarioId}
          onClose={() => setSetDepthOpen(false)}
        />
      )}
      {hasDataError && (
        <Alert
          message="Error Loading Stick Data"
          description={
            stickDataError instanceof Error
              ? stickDataError.message
              : "Failed to fetch stick data from server"
          }
          type="error"
          showIcon
          style={{ margin: "8px 0" }}
        />
      )}
      <GridContainer>
        <Spin
          spinning={
            !hasDataError && canManage && (isLoadingStickData || isDeletingStick)
          }>
          <AutoSizer>
            {({ height, width }) => (
              <DataGrid
                key={scenarioId}
                aria-label="Scenario sticks"
                className="rdg-light"
                columns={columns}
                rows={sticks}
                rowKeyGetter={rowKeyGetter}
                emptyRowsRenderer={EmptySticks}
                rowHeight={36}
                headerRowHeight={36}
                style={{ height, width }}
              />
            )}
          </AutoSizer>
        </Spin>
      </GridContainer>
    </RootContainer>
  );
}
